/* ═══════════════════════════════════════════════════════════════
   The Designer — row 2: the panel buttons, the tools, the Workspace menu.

   TOOL PANEL SYSTEM v2 (docs/DESIGNER_UNIFIED_PLAN.md §2.8, Josh
   2026-08-12). Every tool panel has its own button up here, and that
   button does double duty:

     · CLICK  → the panel drops down ANCHORED beneath the button, full
                UI, portalled. Clicking anywhere outside closes it.
     · DRAG   → drop it into a dock zone to dock it, or let go outside
                every dock zone to leave it floating.

   That pair replaces the old Tools dropdown, and it is what satisfies
   Josh's menu-access-always law: no panel is ever reachable only by
   docking, because its button is always right there.

   What is left of the old menu is a **Workspace** dropdown — the
   workspace-level acts, not the tools: lock the layout, reset it,
   customise the toolbar, and publish the arrangement as the firm's
   default (§2.9, admin-gated).

   The toolbar itself keeps the Bluebeam split: a fixed CORE zone
   (undo/redo, zoom, save) that can never be unpinned, plus a
   customisable zone the user pins and unpins — right-click the row, or
   Workspace ▸ Customise toolbar…. The arrangement is per-user and
   follows them across machines (user_prefs, localStorage as the fast
   cache), same as the dock.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, MoreHorizontal, type LucideIcon } from 'lucide-react';
import { Icon } from '../components/ui/Icon';
import { Prefs } from '../services/prefsService';
import { Button } from '../components/ui/button';
import { Hint } from '../components/ui/tooltip';
import { cn } from '../lib/utils';
import { reconcilePins } from './toolbarPins';
import { appLayerProps } from '../components/ui/app-layer';

/** The three labeled clusters a toolbar reads as (DESIGN.md — top bar
 *  cohesion). Toolboxes = the panel buttons; Editing = the working tools;
 *  Document = the save/publish/history actions, right-justified. Anything
 *  that doesn't say otherwise is Editing — that's where most tools live. */
export type ToolbarSection = 'toolboxes' | 'editing' | 'document';

/** One control (or cluster) the toolbar can show. */
export interface ToolbarItem {
  id: string;
  /** How it is named in the Customise list and the right-click menu. */
  label: string;
  /** Core items are always shown and can never be unpinned. */
  core?: boolean;
  /** Which of the three labeled clusters this renders in. Defaults to
   *  'editing' — everything that isn't a tool panel or a document action. */
  section?: ToolbarSection;
  node: React.ReactNode;
}

/** The ONE button style every top-bar control renders through: a kit
 *  Button, an optional em-sized icon, and a short label, so the whole row
 *  reads as one family. `tooltip` (kit Hint) is how a shortcut rides along
 *  without cluttering the label itself. */
export interface ToolbarButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** A lucide icon component — the common case. */
  icon?: LucideIcon;
  /** Escape hatch for a bespoke inline glyph that isn't a lucide component
   *  (e.g. the hand-drawn track-changes mark). Wins over `icon`. */
  iconNode?: React.ReactNode;
  label: string;
  /** Icon-only rendering for dense composite clusters (undo/redo, marks,
   *  level) — `label` still drives the accessible name (aria-label) and,
   *  absent an explicit `tooltip`, the hover text, so nothing discoverable
   *  is lost by dropping the visible text. */
  hideLabel?: boolean;
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  /** Toggle state — drives aria-pressed and the pressed look. */
  active?: boolean;
  tooltip?: string;
  /** Rendered after the label inside the button (e.g. a waiting-count pill). */
  badge?: React.ReactNode;
}

export const ToolbarButton = React.forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  (
    {
      icon: IconCmp,
      iconNode,
      label,
      hideLabel,
      variant = 'ghost',
      active,
      tooltip,
      badge,
      className,
      'aria-label': ariaLabel,
      ...rest
    },
    ref
  ) => {
    const btn = (
      <Button
        ref={ref}
        type="button"
        variant={variant}
        size="sm"
        className={cn(
          'dsg-tbbtn',
          hideLabel && 'dsg-tbbtn-hide-label',
          active && 'dsg-tbbtn-active',
          className
        )}
        aria-pressed={active}
        aria-label={ariaLabel ?? label}
        {...rest}
      >
        {iconNode ? (
          <span className="dsg-tbbtn-icon" aria-hidden="true">
            {iconNode}
          </span>
        ) : (
          IconCmp && <Icon icon={IconCmp} em={1.1538} aria-hidden="true" />
        )}
        {!hideLabel && <span className="dsg-tbbtn-label">{label}</span>}
        {badge}
      </Button>
    );
    // Icon-only buttons need SOME hover text since the label isn't visible
    // — fall back to the label itself when the caller didn't pass one.
    const tooltipText = tooltip ?? (hideLabel ? label : undefined);
    return tooltipText ? <Hint text={tooltipText}>{btn}</Hint> : btn;
  }
);
ToolbarButton.displayName = 'ToolbarButton';

/** One tool panel, as its top-bar button sees it. */
export interface PanelEntry {
  id: string;
  title: string;
  icon: React.ReactNode;
  /** Where it currently lives — drives the button's pressed state. */
  where: 'docked' | 'floating' | 'dropdown' | null;
  /** Optional count of things waiting inside (Spec Studio's Updates panel).
   *  Zero or absent draws nothing — a badge only ever means "there is work
   *  here", never "this panel exists". */
  badge?: number;
}

export interface DesignerToolbarProps {
  items: ToolbarItem[];
  panels: PanelEntry[];
  /** Click: show this panel — anchored dropdown, or focus where it lives. */
  onPanelClick: (id: string, anchor: { left: number; bottom: number }) => void;
  /** A panel button drag began — the workspace lights its dock targets. */
  onPanelDragStart: (id: string) => void;
  /** …and ended. Outside every dock zone, the panel floats where it landed. */
  onPanelDragEnd: (id: string, x: number, y: number) => void;
  dockLocked: boolean;
  onToggleDockLocked: () => void;
  onResetLayout: () => void;
  /** Publish this arrangement as the firm's default (admin only). */
  onSetFirmDefault?: () => void;
  /** False → the item is shown but disabled, with the reason in its title,
   *  rather than hidden (DESIGN §4: nothing undiscoverable). */
  canSetFirmDefault?: boolean;
  /** Scopes the pinned set to the signed-in user. */
  userId?: string;
  /** Storage-key prefix for the pinned set. Every surface that reuses this
   *  toolbar needs its OWN prefix — sharing the Designer's would silently
   *  inherit that surface's pin customisation (an id unknown to it reads as
   *  "unpinned" instead of "not yet decided"). Defaults to the Designer's
   *  own key so the Designer itself needs no change. */
  storageKeyPrefix?: string;
  /** Workspace ▸ View: how the paper is shown. Absent → no View group. */
  pageView?: 'pages' | 'continuous';
  onPageView?: (view: 'pages' | 'continuous') => void;
}

const toolbarKey = (userId?: string, prefix = 'sf.designerToolbar.v1.') =>
  `${prefix}${userId || 'anon'}`;

/** Automated capture/QA runs sign in as a REAL user. Without this they would
    overwrite that user's cloud-synced toolbar with a throwaway one, and their
    next launch would "revert". Same guard the dock uses. */
const cloudPersistDisabled = () => {
  try {
    return localStorage.getItem('sf.dockNoCloudPersist') === '1';
  } catch {
    return false;
  }
};

/** Read the stored pin list; `null` = the user has never customised it. */
/** One-time id renames for items a past shape of the toolbar merged or
 *  split. A saved pin list is keyed by id, so a rename that isn't mapped
 *  here would silently drop whatever the user had pinned under the old id.
 *  T2 split the Designer's 'review' item into 'track-changes' + 'history'
 *  — anyone who had it pinned keeps both; anyone who had removed it keeps
 *  both removed (the id is simply absent from their saved list either way). */
const PIN_ID_MIGRATIONS: Record<string, string[]> = {
  review: ['track-changes', 'history'],
};

function migratePinIds(pins: string[]): string[] {
  let next = pins;
  for (const [oldId, newIds] of Object.entries(PIN_ID_MIGRATIONS)) {
    if (!next.includes(oldId)) continue;
    const withoutOld = next.filter((id) => id !== oldId);
    const additions = newIds.filter((id) => !withoutOld.includes(id));
    next = [...withoutOld, ...additions];
  }
  return next;
}

function readPins(userId?: string, prefix?: string): string[] | null {
  const key = toolbarKey(userId, prefix);
  const fromPrefs = Prefs.get(key);
  if (Array.isArray(fromPrefs)) return migratePinIds(fromPrefs as string[]);
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return migratePinIds(parsed as string[]);
    }
  } catch {
    /* private mode / corrupt — fall through to the default */
  }
  return null;
}

/* ─── The menus. Portalled, mousedown-outside + Esc to dismiss, clamped
       into the viewport — DESIGN §4 "context UI always on top". ─── */

function Menu({
  anchor,
  label,
  width = 244,
  onClose,
  children,
}: {
  anchor: { left: number; bottom: number };
  label: string;
  width?: number;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [onClose]);
  React.useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, []);

  const maxH = Math.min(460, window.innerHeight - anchor.bottom - 16);
  const left = Math.min(anchor.left, window.innerWidth - width - 8);
  return createPortal(
    <div
      {...appLayerProps}
      ref={ref}
      className="dsg-menu"
      role="menu"
      aria-label={label}
      style={{ left, top: anchor.bottom + 4, width, maxHeight: maxH }}
    >
      {children}
    </div>,
    document.body
  );
}

/* ─── One tool panel's button ───
   Click opens it anchored; drag docks or floats it. Both affordances live on
   the same 32px target, and the title spells out both so nobody has to
   discover the drag. */

function PanelButton({
  panel,
  locked,
  onClick,
  onDragStart,
  onDragEnd,
}: {
  panel: PanelEntry;
  /** The workspace lock is the existing law: locked = clean work view,
   *  nothing to knock out of place. So the DRAG half of the button is off
   *  while locked, and the tooltip says why and where the switch is. The
   *  CLICK half — the access guarantee — never depends on it. */
  locked: boolean;
  onClick: (anchor: { left: number; bottom: number }) => void;
  onDragStart: () => void;
  onDragEnd: (x: number, y: number) => void;
}) {
  const open = panel.where !== null;
  return (
    <button
      type="button"
      className={`dsg-panel-btn${open ? ' dsg-tool-on' : ''}`}
      // So a surface can open a panel by id — the Studio's paper opens
      // "Section End" when the end-of-section line is clicked, the way the
      // classic canvas's onEditSpecial('{EndOfSection}') did.
      data-panel-id={panel.id}
      draggable={!locked}
      aria-pressed={open}
      aria-haspopup="dialog"
      title={
        panel.badge
          ? `${panel.title} — ${panel.badge} waiting. Click to open it here.`
          : locked
            ? `${panel.title} — click to open it here. To dock or float it, unlock the workspace first (Workspace ▸ Unlock).`
            : `${panel.title} — click to open it here, or drag it to dock or float it`
      }
      onClick={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        onClick({ left: r.left, bottom: r.bottom });
      }}
      onDragStart={(e) => {
        // The dock already accepts this exact payload from its palette tray,
        // so dragging a button into the workspace docks it with no new
        // drop-handling code (FormaDock.handleDrop).
        e.dataTransfer.setData('application/x-forma-palette', panel.id);
        e.dataTransfer.setData('text/plain', panel.title);
        e.dataTransfer.effectAllowed = 'copyMove';
        onDragStart();
      }}
      onDragEnd={(e) => onDragEnd(e.clientX, e.clientY)}
    >
      <span className="dsg-panel-btn-icon" aria-hidden="true">
        {panel.icon}
      </span>
      <span className="dsg-panel-btn-label">{panel.title}</span>
      {!!panel.badge && (
        <span className="sps-count-pill">
          {panel.badge}
          <span className="sr-only"> waiting</span>
        </span>
      )}
    </button>
  );
}

export function DesignerToolbar({
  items,
  panels,
  onPanelClick,
  onPanelDragStart,
  onPanelDragEnd,
  dockLocked,
  onToggleDockLocked,
  onResetLayout,
  onSetFirmDefault,
  canSetFirmDefault = false,
  userId,
  storageKeyPrefix,
  pageView = 'continuous',
  onPageView,
}: DesignerToolbarProps) {
  const customIds = React.useMemo(() => items.filter((i) => !i.core).map((i) => i.id), [items]);

  // Default = everything pinned. A user who has never customised the toolbar
  // sees the whole set, and a control shipped later joins it automatically
  // (an unknown id is simply "not yet unpinned").
  const [pins, setPins] = React.useState<string[] | null>(() => readPins(userId, storageKeyPrefix));
  React.useEffect(() => setPins(readPins(userId, storageKeyPrefix)), [userId, storageKeyPrefix]);
  React.useEffect(
    () => Prefs.subscribe(() => setPins(readPins(userId, storageKeyPrefix))),
    [userId, storageKeyPrefix]
  );

  const isPinned = React.useCallback(
    (id: string) => (pins === null ? true : pins.includes(id)),
    [pins]
  );

  const savePins = React.useCallback(
    (next: string[]) => {
      setPins(next);
      const key = toolbarKey(userId, storageKeyPrefix);
      if (!cloudPersistDisabled()) Prefs.set(key, next);
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* private mode — the server copy still carries the preference */
      }
    },
    [userId, storageKeyPrefix]
  );

  // Additive: an item this saved pin set has never been offered joins it once
  // (see toolbarPins.ts); afterwards the user's choice stands.
  React.useEffect(() => {
    if (pins === null || cloudPersistDisabled()) return;
    const seenKey = `${toolbarKey(userId, storageKeyPrefix)}.seen`;
    const stored = Prefs.get(seenKey);
    const seen = Array.isArray(stored) ? (stored as string[]) : null;
    const r = reconcilePins(pins, seen, customIds);
    if (!r.changed) return;
    Prefs.set(seenKey, r.seen);
    if (r.pins !== pins) savePins(r.pins);
  }, [pins, customIds, userId, storageKeyPrefix, savePins]);

  const togglePin = React.useCallback(
    (id: string) => {
      const current = pins === null ? customIds : pins;
      savePins(current.includes(id) ? current.filter((x) => x !== id) : [...current, id]);
    },
    [pins, customIds, savePins]
  );

  const [wsAnchor, setWsAnchor] = React.useState<{ left: number; bottom: number } | null>(null);
  const [ctxMenu, setCtxMenu] = React.useState<{
    left: number;
    bottom: number;
    itemId?: string;
    itemLabel?: string;
  } | null>(null);
  const [customiseOpen, setCustomiseOpen] = React.useState(false);
  // Publishing a firm default changes what every colleague who has never
  // arranged their own workspace opens into. That reaches past the person
  // clicking it, so it confirms first (DESIGN §4).
  const [confirmFirm, setConfirmFirm] = React.useState(false);

  const onContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    const host = (e.target as HTMLElement).closest<HTMLElement>('[data-tb-item]');
    const id = host?.getAttribute('data-tb-item') || undefined;
    const item = id ? items.find((i) => i.id === id) : undefined;
    setCtxMenu({
      left: e.clientX,
      bottom: e.clientY,
      itemId: item && !item.core ? item.id : undefined,
      itemLabel: item?.label,
    });
  };

  const shown = items.filter((i) => i.core || isPinned(i.id));
  const editingItems = React.useMemo(
    () => shown.filter((i) => (i.section ?? 'editing') === 'editing'),
    [shown]
  );
  const documentItems = React.useMemo(() => shown.filter((i) => i.section === 'document'), [shown]);

  /* ─── Responsive overflow: the Editing cluster never clips or wraps onto
     the canvas. Its row is flex-nowrap + overflow:hidden, so scrollWidth vs
     clientWidth tells us whether it's overflowing; trailing items move into
     a "More" menu one at a time until it fits, and grow back only once
     there's real slack (hysteresis — no flicker at the boundary). ─── */
  const editingRowRef = React.useRef<HTMLDivElement>(null);
  const [overflowCount, setOverflowCount] = React.useState(0);
  const [resizeTick, setResizeTick] = React.useState(0);
  const [moreAnchor, setMoreAnchor] = React.useState<{ left: number; bottom: number } | null>(null);

  React.useEffect(() => {
    const el = editingRowRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setResizeTick((t) => t + 1));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  React.useLayoutEffect(() => {
    const el = editingRowRef.current;
    if (!el) return;
    const HYSTERESIS = 24;
    const overflowing = el.scrollWidth > el.clientWidth + 1;
    if (overflowing && overflowCount < editingItems.length) {
      setOverflowCount((c) => Math.min(c + 1, editingItems.length));
    } else if (!overflowing && overflowCount > 0) {
      const slack = el.clientWidth - el.scrollWidth;
      if (slack > HYSTERESIS) setOverflowCount((c) => Math.max(c - 1, 0));
    }
  }, [overflowCount, editingItems, resizeTick]);

  // The item list shrank (e.g. fewer pins) — don't strand an overflow count
  // past the end of the array.
  React.useEffect(() => {
    setOverflowCount((c) => Math.min(c, editingItems.length));
  }, [editingItems.length]);

  // Eviction priority: trailing NON-core items go into "More" first — Undo,
  // Redo, Zoom and any other core control stay on the bar the longest, only
  // evicted once every optional item is already gone.
  const evictionOrder = React.useMemo(() => {
    const nonCore = editingItems.filter((i) => !i.core);
    const core = editingItems.filter((i) => i.core);
    return [...nonCore].reverse().concat([...core].reverse());
  }, [editingItems]);
  const evictedIds = React.useMemo(
    () => new Set(evictionOrder.slice(0, overflowCount).map((i) => i.id)),
    [evictionOrder, overflowCount]
  );
  const visibleEditingItems = editingItems.filter((i) => !evictedIds.has(i.id));
  const overflowedEditingItems = editingItems.filter((i) => evictedIds.has(i.id));

  return (
    <div
      className="dsg-toolbar"
      role="toolbar"
      aria-label="Editor tools"
      onContextMenu={onContextMenu}
    >
      {panels.length > 0 && (
        <div className="dsg-tb-section dsg-tb-section-toolboxes" data-section="toolboxes">
          <span className="dsg-tb-section-label">Toolboxes</span>
          <div className="dsg-panel-bar" role="group" aria-label="Tool panels">
            {panels.map((p) => (
              <PanelButton
                key={p.id}
                panel={p}
                locked={dockLocked}
                onClick={(anchor) => onPanelClick(p.id, anchor)}
                onDragStart={() => onPanelDragStart(p.id)}
                onDragEnd={(x, y) => onPanelDragEnd(p.id, x, y)}
              />
            ))}
          </div>
        </div>
      )}

      {panels.length > 0 && editingItems.length > 0 && (
        <span className="dsg-toolbar-sep" aria-hidden="true" />
      )}

      {editingItems.length > 0 && (
        <div className="dsg-tb-section dsg-tb-section-editing" data-section="editing">
          <span className="dsg-tb-section-label">Editing</span>
          <div className="dsg-tb-row" ref={editingRowRef}>
            {visibleEditingItems.map((item) => (
              <span key={item.id} data-tb-item={item.id} className="dsg-tb-slot">
                {item.node}
              </span>
            ))}
            {overflowCount > 0 && (
              <ToolbarButton
                icon={MoreHorizontal}
                label="More"
                aria-label="More editing tools"
                aria-haspopup="menu"
                aria-expanded={!!moreAnchor}
                onClick={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  setMoreAnchor((a) => (a ? null : { left: r.left, bottom: r.bottom }));
                }}
              />
            )}
          </div>
        </div>
      )}

      {moreAnchor && (
        <Menu anchor={moreAnchor} label="More editing tools" onClose={() => setMoreAnchor(null)}>
          <div className="dsg-tb-more-list">
            {overflowedEditingItems.map((item) => (
              <div key={item.id} data-tb-item={item.id} className="dsg-tb-more-row">
                {item.node}
              </div>
            ))}
          </div>
        </Menu>
      )}

      {documentItems.length > 0 && (
        <div className="dsg-tb-section dsg-tb-section-document" data-section="document">
          <span className="dsg-tb-section-label">Document</span>
          <div className="dsg-tb-row">
            {documentItems.map((item) => (
              <span key={item.id} data-tb-item={item.id} className="dsg-tb-slot">
                {item.node}
              </span>
            ))}
          </div>
        </div>
      )}

      <button
        type="button"
        className="dsg-btn dsg-workspace-btn"
        aria-haspopup="menu"
        aria-expanded={!!wsAnchor}
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setWsAnchor((a) => (a ? null : { left: r.left, bottom: r.bottom }));
        }}
        title="Lock, reset, and share this workspace arrangement"
      >
        Workspace
        <Icon icon={ChevronDown} em={0.9231} aria-hidden="true" />
      </button>

      {wsAnchor && (
        <Menu anchor={wsAnchor} label="Workspace" width={276} onClose={() => setWsAnchor(null)}>
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={!dockLocked}
            className="dsg-menu-item"
            onClick={() => {
              onToggleDockLocked();
              setWsAnchor(null);
            }}
          >
            {dockLocked ? 'Unlock to arrange panels' : 'Lock the workspace'}
          </button>
          <button
            type="button"
            role="menuitem"
            className="dsg-menu-item"
            onClick={() => {
              setCustomiseOpen(true);
              setWsAnchor(null);
            }}
          >
            Customise toolbar…
          </button>
          <button
            type="button"
            role="menuitem"
            className="dsg-menu-item"
            onClick={() => {
              onResetLayout();
              setWsAnchor(null);
            }}
            title="Put every panel back where your firm's default puts it"
          >
            Reset workspace layout
          </button>
          {onPageView && (
            <>
              <div className="dsg-menu-sep" role="separator" />
              <div className="dsg-menu-head" role="presentation">
                View
              </div>
              <div role="group" aria-label="View">
                {(
                  [
                    [
                      'pages',
                      'Pages',
                      'Show the document as separate pages, with each page’s header and footer',
                    ],
                    ['continuous', 'Continuous', 'Show the document as one continuous sheet'],
                  ] as const
                ).map(([id, label, hint]) => (
                  <button
                    key={id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={pageView === id}
                    className={cn('dsg-menu-item', pageView === id && 'is-current')}
                    title={hint}
                    onClick={() => {
                      onPageView(id);
                      setWsAnchor(null);
                    }}
                  >
                    {label}
                    {pageView === id && <Icon icon={Check} em={0.9231} aria-hidden="true" />}
                  </button>
                ))}
              </div>
            </>
          )}
          <div className="dsg-menu-sep" role="separator" />
          <button
            type="button"
            role="menuitem"
            className="dsg-menu-item"
            disabled={!canSetFirmDefault}
            onClick={() => {
              setConfirmFirm(true);
              setWsAnchor(null);
            }}
            title={
              canSetFirmDefault
                ? 'Everyone who has never arranged this workspace themselves will see it like this'
                : 'Only an administrator can change what the rest of the firm sees'
            }
          >
            Set as firm default
          </button>
        </Menu>
      )}

      {ctxMenu && (
        <Menu anchor={ctxMenu} label="Toolbar" onClose={() => setCtxMenu(null)}>
          {ctxMenu.itemId && (
            <button
              type="button"
              role="menuitem"
              className="dsg-menu-item"
              onClick={() => {
                togglePin(ctxMenu.itemId!);
                setCtxMenu(null);
              }}
            >
              Remove “{ctxMenu.itemLabel}” from the toolbar
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className="dsg-menu-item"
            onClick={() => {
              setCustomiseOpen(true);
              setCtxMenu(null);
            }}
          >
            Customise toolbar…
          </button>
        </Menu>
      )}

      {confirmFirm && (
        <ConfirmDialog
          title="Set this as the firm default?"
          body="Everyone in the firm who has never arranged this workspace themselves will open into the layout you are looking at now. Anyone who has their own arrangement keeps it."
          confirmLabel="Set as firm default"
          onConfirm={() => {
            setConfirmFirm(false);
            onSetFirmDefault?.();
          }}
          onClose={() => setConfirmFirm(false)}
        />
      )}

      {customiseOpen && (
        <CustomiseDialog
          items={items}
          isPinned={isPinned}
          onToggle={togglePin}
          onResetPins={() => savePins(customIds)}
          onClose={() => setCustomiseOpen(false)}
        />
      )}
    </div>
  );
}

/* ─── Confirm a change that reaches past the person making it ─── */

function ConfirmDialog({
  title,
  body,
  confirmLabel,
  onConfirm,
  onClose,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !ref.current) return;
      const focusable = [...ref.current.querySelectorAll<HTMLElement>('button:not(:disabled)')];
      if (focusable.length < 2) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      returnTo?.focus?.();
    };
  }, [onClose]);

  return createPortal(
    <div
      {...appLayerProps}
      className="dsg-modal-scrim"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="dsg-modal dsg-modal-confirm" ref={ref}>
        <h2 className="dsg-modal-title">{title}</h2>
        <p className="dsg-modal-sub">{body}</p>
        <div className="dsg-desk-actions">
          <button type="button" className="dsg-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="dsg-primary" onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ─── Customise toolbar — one checkbox row per removable control ─── */

function CustomiseDialog({
  items,
  isPinned,
  onToggle,
  onResetPins,
  onClose,
}: {
  items: ToolbarItem[];
  isPinned: (id: string) => boolean;
  onToggle: (id: string) => void;
  onResetPins: () => void;
  onClose: () => void;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  // Focus moves in, is trapped while open, and returns to whatever opened the
  // dialog (DESIGN §7).
  React.useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !ref.current) return;
      const focusable = [...ref.current.querySelectorAll<HTMLElement>('button:not(:disabled)')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      returnTo?.focus?.();
    };
  }, [onClose]);

  return createPortal(
    <div
      {...appLayerProps}
      className="dsg-modal-scrim"
      role="dialog"
      aria-modal="true"
      aria-label="Customise toolbar"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="dsg-modal" ref={ref}>
        <h2 className="dsg-modal-title">Customise toolbar</h2>
        <p className="dsg-modal-sub">
          Pick the controls you want on the toolbar. Undo, redo, zoom and Save stay put — and every
          tool panel keeps its own button, pinned or not.
        </p>
        <div className="dsg-customise-list">
          {(
            [
              ['editing', 'Editing'],
              ['document', 'Document'],
            ] as const
          ).map(([section, heading]) => {
            const group = items.filter((item) => (item.section ?? 'editing') === section);
            if (!group.length) return null;
            return (
              <div key={section} className="dsg-customise-group">
                <div className="dsg-customise-group-label">{heading}</div>
                {group.map((item) => {
                  const on = isPinned(item.id);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="checkbox"
                      aria-checked={item.core ? true : on}
                      disabled={item.core}
                      className="dsg-rsp-check"
                      onClick={() => onToggle(item.id)}
                    >
                      <span className={`dsg-rsp-box${item.core || on ? ' is-on' : ''}`}>
                        {(item.core || on) && (
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M20 6 9 17l-5-5" />
                          </svg>
                        )}
                      </span>
                      <span className="dsg-rsp-check-text">
                        <span className={`dsg-rsp-check-label${on ? ' is-on' : ''}`}>
                          {item.label}
                        </span>
                        {item.core && (
                          <span className="dsg-rsp-check-sub">Always on the toolbar</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
        <div className="dsg-desk-actions">
          <button type="button" className="dsg-btn" onClick={onResetPins}>
            Show all
          </button>
          <button type="button" className="dsg-primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
